/* eslint-disable no-console */
import * as OT from '@opentok/client';
import { setBandwidth } from '../logger';
import { ConnectedUser } from '../../connection/socket';
import printOTDebugLogForStreamId from '../opentok-debug-helper';
import { GreenroomUser } from '../../stores/reducers/session';
import { AVHookInterface } from '../../contexts/audio-video';
import { OPENTOK_CONSTANTS } from '../../types/constants';
import { appLogger } from '../datadog';
import { LogLevel } from '../../stores/types/types';
import { getLiveDevices } from './video-streams';
import store from '../../stores/main';

const BRANDLIVE_OPENTOK_API_KEY = process.env.REACT_APP_OPENTOK_API_KEY ?? '';
const isDebug = window.location.search.includes('debug=opentok');
let badReload = false;

interface Session extends OT.Session {
	isConnected: Function;
}

interface Publisher extends OT.Publisher {
	streamId: string | number;
	primaryStreamId: string | number;
	/* eslint-disable-next-line no-unused-vars */
	getStats(callback: (error?: OT.OTError, stats?: OT.PublisherStatsArr) => void): void;
	isLoading: () => boolean;
	_: {
		webRtcStream: Function;
	}
}

export interface Subscriber extends OT.Subscriber {
	streamId: string;
	_: {
		unblockAudio: Function;
	}
}

interface RawStats {
	id: string;
	videoBits: number;
	videoPackets: number;
	videoPacketsLost: number;
	audioBits: number;
	audioPackets: number;
	audioPacketsLost: number;
	frames: number;
	lastTime: number;
}

interface LastStat {
	videoBits: number,
	videoPackets: number;
	videoPacketsLost: number;
	audioBits: number;
	audioPackets: number;
	audioPacketsLost: number;
	frames: number;
	time: number;
}

interface LastStats {
	[key: string]: LastStat;
}

interface OTStats {
	videoBitrate: number;
	videoPacketLossRatio: number;
	audioBitrate: number;
	audioPacketLossRatio: number;
	frameRate: number;
	resolution: OT.Dimensions;
	guid: string;
}

export interface TotalOTStats {
	[key: string]: OTStats;
}

export interface VideoQuality {
	width: number;
	height: number;
	frameRate: number;
}

function debugLog(tag: string, ...args: any[]) {
	if (isDebug) {
		console.log(`%c${tag}\n`, 'color: red; font-weight: bold', ...args);
	}
}

export type Subscribers = {
	[key: string]: Subscriber;
}

const asyncScreenshareData = (): Promise<boolean> => new Promise((res) => {
	OT.checkScreenSharingCapability((r: OT.ScreenSharingCapabilityResponse) => res(r?.supported));
});

const asyncStats = (subscriber: Subscriber): Promise<RawStats> => new Promise((resolve) => {
	subscriber?.getStats((error?: OT.OTError, stats?: OT.SubscriberStats) => {
		if (stats?.video) {
			resolve({
				id: String(subscriber.streamId),
				videoBits: stats!.video.bytesReceived * 8 || 0,
				videoPackets: stats!.video.packetsReceived || 0,
				videoPacketsLost: stats!.video.packetsLost || 0,
				audioBits: stats!.audio.bytesReceived * 8 || 0,
				audioPackets: stats!.audio.packetsReceived || 0,
				audioPacketsLost: stats!.audio.packetsLost || 0,
				frames: 0,
				lastTime: Date.now(),
			});
		} else {
			resolve({
				id: String(subscriber.streamId),
				videoBits: 0,
				videoPackets: 0,
				videoPacketsLost: 0,
				audioBits: 0,
				audioPackets: 0,
				audioPacketsLost: 0,
				frames: 0,
				lastTime: Date.now(),
			});
		}
	});
});

const asyncStatsPub = (
	publisher: Publisher,
): Promise<RawStats> => new Promise((resolve) => {
	publisher?.getStats((error?: OT.OTError, stats?: OT.PublisherStatsArr) => {
		if (stats && stats[0]?.stats) {
			resolve({
				id: String(publisher.streamId),
				videoBits: (stats[0].stats?.video.bytesSent ?? 0) * 8,
				videoPackets: stats[0].stats.video.packetsSent || 0,
				videoPacketsLost: stats[0].stats.video.packetsLost || 0,
				audioBits: (stats[0].stats?.audio.bytesSent ?? 0) * 8,
				audioPackets: stats[0].stats.audio.packetsSent || 0,
				audioPacketsLost: stats[0].stats.audio.packetsLost || 0,
				frames: 0,
				lastTime: Date.now(),
			});
		} else {
			resolve({
				id: String(publisher.streamId),
				videoBits: 0,
				videoPackets: 0,
				videoPacketsLost: 0,
				audioBits: 0,
				audioPackets: 0,
				audioPacketsLost: 0,
				frames: 0,
				lastTime: Date.now(),
			});
		}
	});
});

const getPubOrSubStats = (
	pubOrSub: Subscriber | OT.Publisher,
): Promise<OT.PublisherStatsArr | OT.SubscriberStats | undefined> => new Promise((resolve) => {
	pubOrSub?.getStats((err, stats) => {
		if (err || !stats) resolve(undefined);
		resolve(stats);
	});
});

export const getCurrentVirtualBG = (): OT.VideoFilter | undefined => {
	const {
		ContentReducer: { selectedVBG },
	} = store.getState();
	if (selectedVBG?.blur) {
		return {
			type: 'backgroundBlur',
			blurStrength: selectedVBG.blur,
		};
	}
	if (selectedVBG?.url) {
		return {
			type: 'backgroundReplacement',
			backgroundImgUrl: selectedVBG.url,
		};
	}
	return undefined;
};

export default class OpenTok {
	public user: GreenroomUser | null;

	public session: Session;

	public publisher: Publisher | null = null;

	public screensharePublisher: Publisher | null = null;

	public subscribers: Subscribers;

	private lastSubscriberState: Subscribers;

	public onVideoElementCreated: (event: any, isPublisher: boolean) => any;

	private readonly onStats: (event: any) => any;

	private readonly onPublisherRemoved: (event: any) => any;

	private readonly debugListener: (event: any) => any;

	private readonly token: string;

	public sharingScreen: boolean = false;

	private lastStats: LastStats = {};

	public stats: TotalOTStats = {};

	private statsInterval: NodeJS.Timeout;

	public lastRes: number[] = [1920, 1080];

	public lastFrameRate: number = 30;

	public videoMediaStream: MediaStream | null = null;

	public debugOpentokSubscriptions: boolean = false;

	public isEmbed: boolean = false;

	private setAV: AVHookInterface['setAV'];

	private removeAV: AVHookInterface['removeAV'];

	public streams: Map<string, OT.Stream> = new Map<string, OT.Stream>();

	// set of publishers this client has initialized
	public myPublishers: Array<OT.Publisher> = [];
	// private myPublishers: Map<string, OT.Publisher> = new Map<string, OT.Publisher>();

	public checkScreenshare: () => Promise<boolean> = asyncScreenshareData;

	private getSubscriberStats: (subscriber: Subscriber) => Promise<RawStats> = asyncStats;

	private getPublisherStats: (publisher: Publisher) => Promise<RawStats> = asyncStatsPub;

	constructor(
		user: GreenroomUser | null,
		sessionId: string,
		token: string,
		setAVState: AVHookInterface['setAV'],
		removeAVState: AVHookInterface['removeAV'],
		onVideoElementCreated: (event: any, isPublisher: boolean) => any,
		onStats: (event: any) => any,
		onPublisherRemoved?: (event: any) => any,
		debugOpentokSubscriptions: boolean = false,
		isEmbed: boolean = false,
	) {
		this.user = user;
		this.token = badReload ? 'badtoken' : token;
		this.session = OT.initSession(BRANDLIVE_OPENTOK_API_KEY, sessionId) as Session;
		this.session.on('sessionConnected', () => this.sessionListeners());
		this.onVideoElementCreated = onVideoElementCreated;
		this.onStats = onStats;
		this.onPublisherRemoved = onPublisherRemoved || (() => { });
		this.debugListener = this.debugHotkey.bind(this);
		// update the bitrate stats every 5 seconds
		this.statsInterval = setTimeout(this.checkStats, 1000);
		this.debugOpentokSubscriptions = debugOpentokSubscriptions;
		this.isEmbed = isEmbed;
		this.setAV = setAVState;
		this.removeAV = removeAVState;
		this.subscribers = {};
		this.lastSubscriberState = {};

		if (isDebug) {
			// When debugging with the 'debug=opentok' query string,
			// you can simulate a continuous failing SOCKET connection
			// with the E hotkey and an immediate reconnection with the R hotkey.
			window.addEventListener('keydown', this.debugListener);
		}
	}

	log(
		level: LogLevel,
		message: string,
		logData: { [key: string]: any,
	},
	) {
		const commonLogInfo = `OpenTok: [${this.publisher?.stream?.streamId}] ${this?.user?.email}`;
		if (isDebug) {
			console.log(`${commonLogInfo} ${message}`);
		}

		const ddLog = {
			family: 'OpenTok',
			location: 'opentok-connection',
			...logData,
		};

		if (appLogger?.[level]) {
			appLogger[level](`${commonLogInfo} ${message}`, ddLog);
		} else {
			appLogger.info(`${commonLogInfo} ${message}`, ddLog);
		}
	}

	publisherTrackEndLog = (trackType: string, streamId: string) => {
		this.log(
			'info',
			`publisher ${trackType} has ended - streamId: ${streamId}`,
			{ function: `publisher.on videoElementCreated => ${trackType} ended` },
		);
	};

	publisherListeners(publisher: OT.Publisher, isScreensharePublisher: boolean) {
		// sets up listeners for a session or publisher
		publisher.on('streamCreated', (e) => {
			this.onAVDataUpdate(e);
			this.streams.set(e.stream.streamId, e.stream);
		});
		publisher.on('videoElementCreated', (e) => {
			if (e.element instanceof HTMLVideoElement && e.element.srcObject instanceof MediaStream) {
				const [videoTrack] = e.element.srcObject.getVideoTracks();
				const [audioTrack] = e.element.srcObject.getAudioTracks();
				videoTrack?.addEventListener(
					'ended',
					() => this.publisherTrackEndLog('videoTrack', publisher?.stream?.streamId ?? 'unknown'),
					{ once: true },
				);
				audioTrack?.addEventListener(
					'ended',
					() => this.publisherTrackEndLog('audioTrack', publisher?.stream?.streamId ?? 'unknown'),
					{ once: true },
				);
			}
			this.onVideoElementCreated(e, !isScreensharePublisher);
		});
		publisher.on('streamDestroyed', (e) => {
			const { stream } = e as unknown as any;
			if (!stream) return;

			if (stream.streamId) {
				this.removeAV(stream.streamId);
				this.streams.delete(stream.streamId);
			} else {
				console.log('ERROR - no stream id when streamDestroyed');
			}

			this.onPublisherRemoved(e);
			publisher.off();
		});
		publisher.on('muteForced', () => {
			const currAudio = publisher?.stream?.hasAudio;
			publisher.publishAudio(!currAudio);
		});
	}

	signalListeners() {
		this.session.on('signal', (e: any) => {
			try {
				const event = e;
				event.data = JSON.parse(e.data);
				const signalEvent = event.type.split('signal:')[1];
				switch (signalEvent) {
					case OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_AUDIO: {
						const { streamId, value } = event.data;
						const publisher = this.myPublishers.find((p) => p.stream?.streamId === streamId);
						publisher?.publishAudio(value);
						break;
					}
					case OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_VIDEO: {
						const { streamId, value } = event.data;
						const publisher = this.myPublishers.find((p) => p.stream?.streamId === streamId);
						publisher?.publishVideo(value);
						break;
					}
					default: {
						break;
					}
				}
			} catch (exception) {
				if (this.debugOpentokSubscriptions) {
					console.log(exception);
				}
			}
		});
	}

	sessionListeners() {
		this.signalListeners();
		this.session.on('streamCreated', (e) => {
			this.onAVDataUpdate(e);
			this.streams.set(e.stream.streamId, e.stream);
		});
		this.session.on('streamDestroyed', (e) => {
			this.removeAV(e.stream.streamId);
			this.streams.delete(e.stream.streamId);
		});
		this.session.on('streamPropertyChanged', (e) => {
			this.onAVDataUpdate(e);
			this.log(
				'info',
				`streamPropertyChanged ${e.changedProperty} ${e.newValue} ${e.oldValue}`,
				{
					function: 'streamPropertyChanged',
				},
			);
			if (e.changedProperty === 'hasVideo'
				&& this.publisher
				&& this.publisher?.stream?.videoDimensions
				&& e.stream.streamId === this.publisher?.stream?.streamId
				&& (
					(
						this.lastRes[0] !== this.publisher?.stream?.videoDimensions.width
						&& this.lastRes[1] !== this.publisher?.stream?.videoDimensions.height
					)
					|| this.lastFrameRate !== this.publisher?.stream?.frameRate
				)
			) {
				this.videoMediaStream?.getVideoTracks()[0]?.applyConstraints(
					{
						width: { ideal: this.lastRes[0] },
						height: { ideal: this.lastRes[1] },
						frameRate: { exact: this.lastFrameRate },
					},
				);
			}
		});
	}

	onAVDataUpdate(e: any) {
		if (e?.stream?.id) {
			const avData = {
				audio: e.stream.hasAudio ?? false,
				video: e.stream.hasVideo ?? false,
				id: e.stream.id === this.publisher?.stream?.streamId
					? 'local'
					: e.stream.id,
			};
			this.setAV(avData);
		}
	}

	debugHotkey(e: any) {
		if (e.repeat) return;

		if (e.code === 'KeyE') {
			// disconnect from session and reload with invalid token
			if (this.session.isConnected()) this.session.disconnect();
			badReload = !badReload;
		} else if (e.code === 'KeyR') {
			// disconnect from session and reload
			if (this.session.isConnected()) this.session.disconnect();
		} else if (e.code === 'KeyT') {
			debugLog(BRANDLIVE_OPENTOK_API_KEY);
		}
	}

	subscribeOnly = () => new Promise((resolve, reject) => {
		this.session.connect(this.token, (err?: OT.OTError) => {
			if (err) reject(err);
			else resolve(true);
		});
	});

	destroy() {
		clearInterval(this.statsInterval);
		this.session.off();
		if (isDebug) {
			window.removeEventListener('keydown', this.debugListener);
		}
	}

	checkStats = async () => {
		this.stats = await this.getOTStats();
		this.onStats(this.stats);
		this.statsInterval = setTimeout(this.checkStats, 5000);
	};

	onStreamAdded = (callback: Function) => {
		if (this.debugOpentokSubscriptions) {
			console.log('stream added');
		}

		this.session.on('streamCreated', (e: any) => {
			callback(e);
		});
	};

	debugOTStreamRemoved = (stream: OT.Stream) => {
		if (this.debugOpentokSubscriptions) {
			const id = stream?.name?.split(',')?.[0] ?? 0;
			const connectedUser: any = window?.debugOT?.connectedUsers?.find(
				(cu: ConnectedUser) => Number(cu.gr_user) === Number(id)
					|| cu?.screenShareWebrtcId === stream.streamId,
			);
			const altText = connectedUser?.screenShareWebrtcId === stream.streamId
				? '\'s Screen Share'
				: '';

			if (this.debugOpentokSubscriptions) {
				console.log(`removed ${
					connectedUser
						? `${connectedUser.first_name} ${connectedUser.last_name}${altText}`
						: stream.streamId
				}`);
			}
		}
	};

	/* eslint-disable-next-line no-unused-vars */
	onStreamRemoved = (callback: (event: any) => void) => {
		this.session.on('streamDestroyed', (e) => {
			this.log(
				'info',
				`stopped subscribing to stream ${e.stream.streamId}`,
				{
					function: 'onStreamRemoved',
				},
			);

			const removed = this.subscribers[e.stream.streamId];

			if (removed) {
				if (this.debugOpentokSubscriptions && removed.stream) {
					this.debugOTStreamRemoved(removed.stream);
				}
				// Remove all listeners from removed stream
				removed.off();
				delete this.subscribers[e.stream.streamId];
			}
			callback(e);
		});
	};

	/* eslint-disable-next-line no-unused-vars */
	onSessionDisconnect = (callback: (event: any) => void) => {
		this.session.on('sessionDisconnected', (e) => {
			this.log(
				'info',
				`disconnected from session ${this.session.sessionId}`,
				{
					function: 'onSessionDisconnect',
				},
			);
			if (this.debugOpentokSubscriptions) console.log('session disconnected ');
			if (this.debugOpentokSubscriptions) console.log('attempting to handle reconnect ');
			callback(e);
		});
	};

	/* eslint-disable-next-line no-unused-vars */
	onReconnected = (callback: (event: any) => void) => {
		this.session.on('reconnected', (e) => {
			this.log(
				'info',
				`reconnected to session ${this.session.sessionId}`,
				{
					function: 'onReconnected',
				},
			);
			if (this.debugOpentokSubscriptions) console.log('session reconnected');
			callback(e);
		});
	};

	debugOTSubscribeToStream = (stream: OT.Stream, resubscribed = false) => {
		if (this.debugOpentokSubscriptions) {
			const id = stream?.name?.split(',')?.[0] ?? 0;
			const connectedUser: any = window?.debugOT?.connectedUsers?.find(
				(cu: ConnectedUser) => Number(cu.gr_user) === Number(id)
					|| cu?.screenShareWebrtcId === stream.streamId,
			);
			const subType = !resubscribed ? 'subscribed' : 'resubscribed';
			const altText = connectedUser?.screenShareWebrtcId === stream.streamId
				? '\'s Screen Share'
				: '';

			if (this.debugOpentokSubscriptions) {
				console.log(`${subType} to ${
					connectedUser
						? `${connectedUser.first_name} ${connectedUser.last_name}${altText}`
						: stream.streamId
				}`);
			}
		}
	};

	subscribeToStream = (
		stream: OT.Stream,
		companionMode: boolean = false,
		embed: boolean = false,
	) => {
		this.log(
			'info',
			`subscribed to stream ${stream.streamId}`,
			{
				function: 'subscribeToStream',
			},
		);

		if (this.debugOpentokSubscriptions) this.debugOTSubscribeToStream(stream);

		return new Promise((resolve, reject) => {
			// We may want to use this for sip
			// const subscriber = this.session.subscribe(stream, isSip ? 'sip-container' : undefined, {
			//	insertDefaultUI: isSip,
			// }
			const subscriber = this.session.subscribe(stream, undefined, {
				insertDefaultUI: false,
				audioVolume: embed ? 0 : 100, // start embed at 0
			}, (error?: OT.OTError) => {
				if (error) {
					this.log(
						'error',
						`subscribed to stream ${stream.streamId} failure with error ${error.name} ${error.message}`,
						{
							function: 'subscribeToStream session.subscribe',
						},
					);
					reject(error);
				} else {
					resolve(true);
				}
			}) as Subscriber;

			subscriber.subscribeToAudio(!companionMode);

			subscriber.on('videoElementCreated', (e) => this.onVideoElementCreated(e, false));
			subscriber.on(
				'videoDisabled',
				(e: any) => {
					debugLog('videoDisabled', e);
					this.log(
						'info',
						`subscriber videoDisabled ${stream.streamId}`,
						{
							function: 'subscribeToStream on videoDisabled',
						},
					);
				},
			);
			subscriber.on(
				'videoEnabled',
				(e: any) => {
					this.log(
						'info',
						`subscriber videoEnabled ${stream.streamId}`,
						{
							function: 'subscribeToStream on videoEnabled',
						},
					);
					debugLog('videoEnabled', e);
				},
			);
			subscriber.on(
				'videoDisableWarning',
				() => {
					this.log(
						'info',
						`subscriber videoDisableWarning ${stream.streamId}`,
						{
							function: 'subscribeToStream on videoDisableWarning',
						},
					);
				},
			);

			this.subscribers = {
				...this.subscribers,
				[stream.streamId]: subscriber,
			};
		});
	};

	resubscribeToStream = async (subscriber: Subscriber) => {
		const { stream } = subscriber;
		if (stream) {
			this.log(
				'info',
				`resubscribing to stream ${stream.streamId}`,
				{
					function: 'resubscribeToStream',
				},
			);

			if (this.debugOpentokSubscriptions) {
				this.debugOTSubscribeToStream(stream, true);
			}

			this.session.unsubscribe(subscriber);
			await this.subscribeToStream(stream);
		}
	};

	mediaStopped = (event: any) => {
		event.preventDefault();

		if (this.debugOpentokSubscriptions) {
			console.log(`event: ${JSON.stringify(event)}`);
		}
	};

	startStream = (
		audioPref: boolean,
		videoPref: boolean,
		selectedAudio?: string,
		selectedVideo?: string,
		name?: string,
		isRefresh: boolean = false, // if the client errors, we check session connection and poll for the new publisher
		quality: VideoQuality = { width: 1920, height: 1080, frameRate: 30 },
		useAdvancedAudio: boolean = false,
	): Promise<Publisher | null> => new Promise((resolve, reject) => {
		const createNewStream = async () => {
			try {
				if (!this.session.isConnected()) {
					await this.startSession();
				}
			} catch (e) {
				this.log('error', `error attempting to publish stream error ${e}`, { function: 'startStream' });
				reject(e);
			}

			let interval: NodeJS.Timeout | undefined;

			this.lastRes = [quality.width, quality.height];
			this.lastFrameRate = quality.frameRate;

			// a GUID (opentok_client_id) can be used to identify a single user with multiple connections.
			// this can only be found in the user's local storage
			// and not on publisher and subscriber objects.
			const guid = localStorage.getItem('opentok_client_id');
			const pubName = name ?? String(this.user?.gr_user);

			// check if user's selected devices can produce video and audio
			const [hasVid, hasAud] = await getLiveDevices(selectedVideo, selectedAudio);
			const videoFilter = getCurrentVirtualBG();

			// if a virtual background exists, cap the feeds resolution at 720p
			// keep a lower resolution if quality is already 720p
			if (videoFilter) {
				quality.width = quality.width > 1280 ? 1280 : quality.width;
				quality.height = quality.height > 720 ? 720 : quality.height;
			}

			this.publisher = OT.initPublisher(undefined, {
				insertDefaultUI: false,
				showControls: false,
				resolution: quality.width === 1920 ? '1920x1080' : '1280x720',
				maxResolution: { width: quality.width, height: quality.height },
				frameRate: 30,
				// enableDtx is false due to this ticket: https://brandlive.atlassian.net/browse/GREEN-5042
				enableDtx: false,
				// this can be set to a MediaStreamTrack,
				// but we can't change cameras - useful if we want to use a canvas to apply effects
				// comma separated string with the user's gr_user id and OpenTok guid.
				name: guid ? `${pubName},${guid}` : pubName,
				height: quality.height,
				width: quality.width,
				audioBitrate: useAdvancedAudio ? 64000 : 40000,
				audioFallbackEnabled: false,
				audioSource: hasAud ? selectedAudio : false,
				videoSource: hasVid ? selectedVideo : false,
				publishAudio: hasAud ? audioPref : false,
				publishVideo: hasVid ? videoPref : false,
				disableAudioProcessing: useAdvancedAudio,
				enableStereo: useAdvancedAudio,
				videoFilter,
			}, (error) => {
				if (error) {
					this.log(
						'error',
						`attempting to publish stream publisher error ${error?.name} ${error?.message}`,
						{ function: 'startStream' },
					);
					console.log(`error publishing OT: ${error?.name} . ${error?.message}`);
				}
			}) as Publisher;

			if (this.publisher) {
				this.myPublishers.push(this.publisher);
				this.publisherListeners(this.publisher, false);
			}

			this.session.publish(this.publisher, (error?: OT.OTError) => {
				if (error) {
					this.log(
						'error',
						`startStream stream publish session error ${error.name} ${error.message}`,
						{ function: 'startStream session.publish' },
					);
					reject(error);
				} else if (isRefresh) {
					console.log('isrefresh');
					interval = setInterval(() => {
						if (this.publisher && this.publisher.streamId) {
							clearInterval(interval as NodeJS.Timeout);

							// this is a hidden method within opentok - returns the outgoing video
							// stream so we can update the resolution
							this.videoMediaStream = this.publisher?._.webRtcStream();
							this.videoMediaStream?.getVideoTracks()[0]?.applyConstraints(
								{
									width: { ideal: quality.width },
									height: { ideal: quality.height },
									frameRate: { ideal: quality.frameRate, max: quality.frameRate },
								},
							);

							resolve(this.publisher);
						}
					}, 150);
				} else {
					resolve(this.publisher);
				}
			});
		};
		createNewStream().catch((e) => reject(e));
	});

	stopPublishing = (streamId?: string) => {
		const publisherToStop = this.myPublishers.find(
			(p) => streamId && p.stream?.streamId === streamId,
		) as Publisher ?? this.publisher;
		if (publisherToStop && !publisherToStop?.isLoading()) {
			this.session.unpublish(publisherToStop);
		}
	};

	getImage = () => this.publisher?.getImgData();

	changeResolution = (height: number, width: number, framerate: number): void => {
		const [lastWidth, lastHeight] = this.lastRes;

		// only update resolution when it's a change, otherwise ignore
		if ((lastWidth !== width || lastHeight !== height) || this.lastFrameRate !== framerate) {
			// set video track resolution by applying constraints to the video track if it exists
			if (this.publisher?._.webRtcStream()) {
				this.videoMediaStream?.getVideoTracks()[0]?.applyConstraints(
					{
						width: { ideal: 1920 },
						height: { ideal: 1080 },
						frameRate: { ideal: framerate, max: framerate },
					},
				);
			}
			this.lastRes = [1920, 1080];
			this.lastFrameRate = framerate;
			debugLog('changing resolution', this.lastRes);
		}
	};

	changeVideoStream = async (selectedVideo: string) => {
		if (!this.publisher) {
			throw new Error('publisher is not set');
		}

		let prevVideo: any;

		try {
			prevVideo = await this.publisher?.getVideoSource();
		} catch (e) {
			console.error('could not get previous video', e);
		}

		// can't set video source on publisher that isn't currently using one
		if (!prevVideo.deviceId) {
			this.stopPublishing();
			return true;
		}

		try {
			await this.publisher!.setVideoSource(selectedVideo);
			this.videoMediaStream = this.publisher?._.webRtcStream();
			return true;
		} catch (e) {
			if (prevVideo?.deviceId) {
				await this.changeVideoStream(prevVideo.deviceId);
			}
			console.error('could not change video', e);
			return false;
		}
	};

	changeAudioStream = async (selectedAudio: string) => {
		if (!this.publisher) {
			throw new Error('publisher is not set');
		}

		let prevAudio: any;

		try {
			prevAudio = await this.publisher?.getAudioSource();
		} catch (e) {
			console.error('could not get previous audio', e);
		}

		// can't set audio source on publisher that isn't currently using one
		if (!prevAudio) {
			this.stopPublishing();
			return true;
		}

		try {
			await this.publisher?.setAudioSource(selectedAudio);
			console.log('setSelectedAudio', selectedAudio);
			return true;
		} catch (e) {
			console.error('could not change audio', e);
		}

		return false;
	};

	stop = () => {
		this.publisher?.destroy();
		this.destroy();
	};

	startSession() {
		return new Promise((resolve, reject) => {
			this.session.connect(this.token, (err) => {
				if (err) reject(err);
				else resolve(true);
			});
		});
	}

	startScreensharing = (
		onDestroyed: () => void,
	) => new Promise((resolve, reject) => {
		this.log(
			'info',
			'publish screensharing',
			{
				function: 'startScreensharing',
			},
		);

		let interval: NodeJS.Timeout | undefined;

		this.screensharePublisher = OT.initPublisher(undefined, {
			insertDefaultUI: false,
			audioFallbackEnabled: false,
			// enableDtx is false due to this ticket: https://brandlive.atlassian.net/browse/GREEN-5042
			enableDtx: false,
			publishAudio: true,
			showControls: false,
			frameRate: 30,
			videoSource: 'screen',
			name: String(this.user?.gr_user),
			audioBitrate: 64000,
			disableAudioProcessing: true,
			enableStereo: true,
		}) as Publisher;

		if (this.screensharePublisher) {
			this.myPublishers.push(this.screensharePublisher);
			this.publisherListeners(this.screensharePublisher, true);
		}

		this.screensharePublisher.on('mediaStopped streamDestroyed', onDestroyed);
		this.session.publish(this.screensharePublisher, (err?: OT.OTError) => {
			if (err) {
				this.log(
					'error',
					`publish screensharing error ${err?.name} ${err?.message}`,
					{
						function: 'startScreensharing',
					},
				);
				reject(err);
			} else {
				interval = setInterval(() => {
					if (this.screensharePublisher!.streamId) {
						clearInterval(interval as NodeJS.Timeout);
						resolve(this.screensharePublisher);
					}
				}, 150);
			}
		});
	});

	stopScreensharing = () => {
		this.log(
			'info',
			'stopping screensharing',
			{
				function: 'stopScreensharing',
			},
		);
		// prop exists on screensharePublisher but the type doesn't include it.
		const id = this.screensharePublisher?.streamId;
		this.screensharePublisher?.destroy();
		return id;
	};

	getRTCFrames = async (
		user: Subscriber | Publisher,
	): Promise<number> => {
		try {
			const getFrames = async () => {
				const userRTCStats = await user.getRtcStatsReport();
				let returnedFrames = 0;
				if (userRTCStats instanceof Map || userRTCStats instanceof RTCStatsReport) {
					userRTCStats.forEach((rep: any) => {
						if ((rep.type === 'track' || rep.type === 'inbound-rtp') && rep.kind === 'video') {
							returnedFrames = rep.framesReceived;
						}
					});
				} else {
					if(Array.isArray(userRTCStats)) {
						userRTCStats[0].rtcStatsReport.forEach((rep: any) => {
							if (rep.type === 'media-source' && rep.kind === 'video') {
								returnedFrames = rep.frames;
							}
						});
					}
				}
				return returnedFrames;
			};
			const frames = await getFrames().catch(() => 0);
			return frames ?? 0;
		} catch (e) {
			this.log(
				'error',
				`getRTCFrames error ${e}`,
				{
					function: 'getRTCFrames',
				},
			);
			console.error(e);
			return 0;
		}
	};

	// get stats for all publishers and subscribers and send them as logs to Datadog
	logOpentokStats = async (): Promise<void> => {
		try {
			if (!this.session.isConnected()) return;
			const opentokStats: { [key: string]: OT.SubscriberStats | OT.PublisherStatsArr } = { };
			const allPubsAndSubs = [...this.myPublishers, ...Object.values(this.subscribers)];

			const promises: Promise<void>[] = [];
			allPubsAndSubs.forEach((pubOrSub) => {
				promises.push(getPubOrSubStats(pubOrSub).then((stats) => {
					if (!stats || !pubOrSub.stream) return;
					opentokStats[pubOrSub.stream.streamId] = stats;
				}));
			});

			await Promise.all(promises);
			appLogger.info('logOpentokStats', { opentokStats });
		} catch (e) {
			appLogger.error(`logOpentokStats error: ${e}`);
		}
	};

	// for bitrate, we can only check the total number of downloaded bytes per subscriber and total
	// number of uploaded bytes for the publisher. so here we get what the current number of
	// downloaded/uploaded bytes is, subtract the last number of downloaded/uploaded bytes from that
	// then divide by the number of seconds since the last check (to get bits per second) and divide
	// by 1024 for kilobits per second.
	parseStats = (stats: RawStats, user: Subscriber | Publisher) => {
		const elapsedSeconds = (stats.lastTime - this.lastStats[stats.id].time) / 1000;

		// packet loss ratio is calculated as a percentage of the packets lost out of the expected
		// packets sent or received. we use the packets lost and sent/received in the current
		// interval (last - current) to provide a more useful metric than total packet loss.
		const periodVideoPacketLoss = stats.videoPacketsLost
			- this.lastStats[stats.id].videoPacketsLost;
		const periodVideoPackets = stats.videoPackets
			- this.lastStats[stats.id].videoPackets;
		const periodAudioPacketLoss = stats.audioPacketsLost
			- this.lastStats[stats.id].audioPacketsLost;
		const periodAudioPackets = stats.audioPackets - this.lastStats[stats.id].audioPackets;

		return {
			videoBitrate: Math.floor(
				(
					(stats.videoBits - this.lastStats[stats.id].videoBits)
					/ elapsedSeconds
				) / 1024,
			),
			videoPacketLossRatio: periodVideoPacketLoss > 0
				? periodVideoPacketLoss / periodVideoPackets
				: 0,
			audioBitrate: Math.floor(
				(
					(stats.audioBits - this.lastStats[stats.id].audioBits)
					/ elapsedSeconds
				) / 1024,
			),
			audioPacketLossRatio: periodAudioPacketLoss > 0
				? periodAudioPacketLoss / periodAudioPackets
				: 0,
			frameRate: ((stats.frames - this.lastStats[stats.id].frames) / elapsedSeconds),
			resolution: user.stream?.hasVideo
				? { width: user.videoWidth() ?? 0, height: user.videoHeight() ?? 0 }
				: { width: 0, height: 0 },
			guid: user.stream?.name?.includes(',') ? user.stream?.name.split(',')[1] : '0',
		};
	};

	printOTDebugData = () => {
		if (this.debugOpentokSubscriptions) {
			if (this.lastSubscriberState !== this.subscribers && window.debugOT) {
				// clean up the subscribes, unsubscribes, resubscribes
				// (as users change we don't want to run oom)
				window.debugOT.resubscribes = {};
				window.debugOT.subscribes = {};
				window.debugOT.unsubscribes = {};
			}

			// logic to allow us to debug each subscriber (updated list)
			Object.entries(this.subscribers).forEach(([, subscriber]) => {
				if (this.debugOpentokSubscriptions && this.subscribers !== this.lastSubscriberState) {
					if (subscriber?.stream) {
						const { stream } = subscriber;
						const { streamId } = subscriber.stream;
						printOTDebugLogForStreamId(
							streamId,
							{
								subscriber,
							},
							() => {
								console.log(`manually resubscribing to ${streamId}`);
								this.resubscribeToStream(subscriber);
							},
							() => {
								console.log(`manually unsubscribing from ${streamId}`);
								this.session.unsubscribe(subscriber);
							},
							() => {
								console.log(`manually subscribing to ${streamId}`);
								this.subscribeToStream(stream);
							},
						);
					}
				}
			});
			this.lastSubscriberState = this.subscribers;
		}
	};

	getOTStats = async (): Promise<TotalOTStats> => {
		const totalStats: TotalOTStats = {};
		try {
			if (this.debugOpentokSubscriptions && window.debugOT) {
				this.printOTDebugData();
				window.debugOT.subscribers = this.subscribers;
			}
			Object.entries(this.subscribers).forEach(async ([, subscriber]) => {
				// OpenTok's getStats method does not work with screensharing
				if (subscriber?.stream?.videoType !== 'screen') {
					const newStats = await this.getSubscriberStats(subscriber);

					newStats.frames = subscriber.stream ? await this.getRTCFrames(subscriber) : 0;

					if (this.lastStats[newStats.id] && newStats.id !== 'null') {
						totalStats[newStats.id] = this.parseStats(newStats, subscriber);
					}

					this.lastStats[newStats.id] = {
						videoBits: newStats.videoBits,
						videoPackets: newStats.videoPackets,
						videoPacketsLost: newStats.videoPacketsLost,
						audioBits: newStats.audioBits,
						audioPackets: newStats.audioPackets,
						audioPacketsLost: newStats.audioPacketsLost,
						frames: newStats.frames,
						time: newStats.lastTime,
					};
				}
			});

			if (this.publisher) {
				const newStat = await this.getPublisherStats(this.publisher);
				newStat.frames = this.publisher?.stream ? await this.getRTCFrames(this.publisher) : 0;

				if (this.lastStats[newStat.id] && newStat.id !== 'null') {
					totalStats[newStat.id] = this.parseStats(newStat, this.publisher);
				}

				this.lastStats[newStat.id] = {
					videoBits: newStat.videoBits,
					videoPackets: newStat.videoPackets,
					videoPacketsLost: newStat.videoPacketsLost,
					audioBits: newStat.audioBits,
					audioPackets: newStat.audioPackets,
					audioPacketsLost: newStat.audioPacketsLost,
					frames: newStat.frames,
					time: newStat.lastTime,
				};

				if (totalStats[this.publisher.streamId]) {
					setBandwidth(totalStats[this.publisher.streamId].videoBitrate);
				}
			}
		} catch (e) {
			this.log(
				'error',
				`getOTStats error ${e}`,
				{
					function: 'getOTStats',
				},
			);
			console.error(e);
		}
		return totalStats;
	};

	/**
	 * This was added to fix a bug where people with muted cam feed not being
	 * heard by people who were in the room before they joined.
	 */
	resubscribeToStreamIfVideoMuted = (streamId: string) => {
		const sub = this.subscribers[streamId];

		if (!sub || !sub?.stream) return false; // this isn't good

		const { stream } = sub;
		const {
			hasVideo,
			// @ts-ignore
			channel: channels,
		} = stream;

		const audioChannel = channels.find((c: { type: string }) => c.type === 'audio');

		if (hasVideo || !audioChannel) return false; // we will not need to resubscribe

		if (this.debugOpentokSubscriptions) {
			console.log('resubscribing to stream with webcam off ', streamId);
		}

		// unsubscribe to old stream
		this.session.unsubscribe(sub);
		if (!this.subscribers[streamId].stream) delete this.subscribers[streamId];

		if (stream) {
			// now subscribe again to them
			this.subscribeToStream(stream);
		}
		return false;
	};

	forceSetAudio = (streamId: string, value: boolean) => {
		// value in this context is true if unmuted, and false if muted (the prescence of audio)
		const stream = this.streams.get(streamId);

		if (stream) {
			this.session.signal({
				type: OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_AUDIO,
				data: JSON.stringify({ streamId, value }),
				to: stream.connection,
			}, () => { });
		}
	};

	forceSetVideo = (streamId: string, value: boolean) => {
		// value in this context is true if unmuted, and false if muted (the prescence of video)
		const stream = this.streams.get(streamId);

		if (stream) {
			this.session.signal({
				type: OPENTOK_CONSTANTS.SIGNALS.FORCE_SET_VIDEO,
				data: JSON.stringify({ streamId, value }),
				to: stream.connection,
			}, () => {});
		}
	};

	setLocalAudio = (active: boolean) => {
		// active is true if audio is unmuted, false if muted
		if (this.debugOpentokSubscriptions) {
			console.log(`OTClass setting local audio to ${active} for publisher: ${this.publisher?.streamId}`);
		}

		// todo this is where we could show an error to the user if publisher is false
		// and not set the local av state - is this something we should do anyways?
		this.publisher?.publishAudio(active);
	};

	setLocalVideo = (active: boolean) => {
		// active is true if audio is unmuted, false if muted
		if (this.debugOpentokSubscriptions) {
			console.log(`OTClass setting local video to ${active} for publisher: ${this.publisher?.streamId}`);
		}
		this.publisher?.publishVideo(active);
	};
}
